// src/pages/SheepGuardianLanding.jsx
import { db } from "../firebase-config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import {
  getAttributionMetadata,
  trackContactSubmit,
  trackWaitlistSubmit,
} from "../utils/analytics";

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

/**
 * Sheep Guardian – One-page Landing
 */

const IMG = {
  hero: "/marketing/hero-signage.webp",
  warning: "/marketing/warning.webp",
  features: "/marketing/features.webp",
  farmer: "/marketing/backgroundFarmer.webp",
  render1: "/marketing/renderfront.webp",
  render2: "/marketing/renderside.webp",
  render3: "/marketing/renderiso.webp",
};

const BRAND = {
  green: "#23493B",
  yellow: "#F6D548",
};

const SOCIAL = {
  x: "https://x.com/SheepGuardianHQ",            // TODO: replace
  facebook: "https://www.facebook.com/ThesheepGuardian/", // TODO: replace
  instagram: "https://www.instagram.com/thesheepguardian/", // TODO: replace
  linkedin: "https://www.linkedin.com/company/SheepGuardian", // TODO: replace
};

function buildAttributionFields() {
  const attribution = getAttributionMetadata();
  const submissionPage = attribution.submissionPage || {};

  return {
    attribution: {
      firstTouch: attribution.firstTouch,
      latestTouch: attribution.latestTouch,
      submissionPage: attribution.submissionPage,
      submittedAt: attribution.submittedAt,
    },
    sourceDomain: submissionPage.hostname || "",
    sourcePath: submissionPage.routePath || submissionPage.pathname || "",
    referrer: submissionPage.referrer || "",
    utmSource: submissionPage.utm_source || "",
    utmMedium: submissionPage.utm_medium || "",
    utmCampaign: submissionPage.utm_campaign || "",
  };
}

function SocialLink({ href, label, children, className = "" }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-white/90 hover:bg-white/10 ${className}`}
    >
      {children}
    </a>
  );
}

function IconX() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
      <path d="M18.9 3H22l-7.8 9.1L22.7 21H16l-5-6.2L5 21H1.9l8.4-9.8L1.5 3H8l4.6 5.7L18.9 3z"/>
    </svg>
  );
}
function IconFacebook() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
      <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.6V12h2.8V9.7c0-2.8 1.7-4.4 4.2-4.4 1.2 0 2.4.2 2.4.2v2.6h-1.4c-1.3 0-1.7.8-1.7 1.6V12h3l-.5 2.9h-2.5v7A10 10 0 0 0 22 12z"/>
    </svg>
  );
}
function IconInstagram() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
      <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm10 2H7a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3zm-5 3a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2.2A2.8 2.8 0 1 0 12 16.8 2.8 2.8 0 0 0 12 9.2zM18 6.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
    </svg>
  );
}
function IconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
      <path d="M20.45 20.45h-3.55v-5.6c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.95v5.69H9.37V9h3.4v1.56h.05c.47-.9 1.6-1.85 3.3-1.85 3.53 0 4.18 2.33 4.18 5.36v6.38zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z"/>
    </svg>
  );
}


const baseNav = [
  { label: "Home", hash: "#home" },
  { label: "Problem", hash: "#problem" },
  { label: "How it works", hash: "#how" },
  { label: "Whats different", hash: "#why-different" },
  { label: "Product", hash: "#product" },
  { label: "Story", hash: "#story" },
  { label: "Join", hash: "#signup" },
];

function Navbar({ rootLinks = false }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHomeRoute = pathname === "/";

  // Smooth-scroll on the home page; otherwise jump to "/?s=<id>" so the landing page hook can scroll.
  const goTo = (id) => (e) => {
    e.preventDefault();
    if (isHomeRoute) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/?s=${id}`);
    }
    setOpen(false);
  };

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[rgba(35,73,59,0.78)] backdrop-blur"
      style={{ backgroundColor: "rgba(35,73,59,0.78)" }}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
        {/* Logo → always route to the home page */}
        <Link to="/" className="group inline-flex items-center gap-3" onClick={() => setOpen(false)}>
          <img src="/logo.png" alt="Sheep Guardian logo" className="h-9 w-9 rounded-xl bg-white p-1 object-contain shadow" />
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-wide text-white">SHEEP GUARDIAN</div>
            <div className="text-[11px] uppercase tracking-wider text-white/70">by BT-BROTECH</div>
          </div>
        </Link>

        <button
          className="inline-flex items-center gap-2 rounded-lg px-2 py-1 text-white md:hidden"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>

        <ul className="hidden items-center gap-6 md:flex">
          {baseNav.map((n) => {
            const id = n.hash.replace("#", "");
            return (
              <li key={n.hash}>
                <a
                  href={isHomeRoute ? `#${id}` : `/?s=${id}`}
                  onClick={goTo(id)}
                  className="text-sm font-medium text-white/90 transition hover:text-white"
                >
                  {n.label}
                </a>
              </li>
            );
          })}
          <li>
            <Link
              to="/contact"
              className="block rounded-lg px-2 py-2 text-sm font-medium text-white/90 hover:bg-white/5"
              onClick={() => setOpen(false)}
            >
              Contact
            </Link>
          </li>
          <li>
            <a
              href={isHomeRoute ? "#signup" : "/?s=signup"}
              onClick={goTo("signup")}
              className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-green-900 shadow hover:shadow-md"
            >
              Join the Launch List
            </a>
          </li>
        </ul>
      </nav>

      {open && (
        <div className="border-t border-white/10 bg-[rgba(35,73,59,0.96)] px-4 py-2 md:hidden">
          <ul className="grid gap-2 pb-3">
            {baseNav.map((n) => {
              const id = n.hash.replace("#", "");
              return (
                <li key={n.hash}>
                  <a
                    href={isHomeRoute ? `#${id}` : `/?s=${id}`}
                    onClick={goTo(id)}
                    className="block rounded-lg px-2 py-2 text-sm font-medium text-white/90 hover:bg-white/5"
                  >
                    {n.label}
                  </a>
                </li>
              );
            })}
            <li className="flex items-center gap-1 pl-2">
              <SocialLink href={SOCIAL.x} label="Follow on X (Twitter)">
                <IconX />
              </SocialLink>
              <SocialLink href={SOCIAL.facebook} label="Follow on Facebook">
                <IconFacebook />
              </SocialLink>
              <SocialLink href={SOCIAL.instagram} label="Follow on Instagram">
                <IconInstagram />
              </SocialLink>
              <SocialLink href={SOCIAL.linkedin} label="Follow on LinkedIn">
                <IconLinkedIn />
              </SocialLink>
            </li>
            <li>
              <Link
                to="/contact"
                className="block rounded-lg px-2 py-2 text-sm font-medium text-white/90 hover:bg-white/5"
                onClick={() => setOpen(false)}
              >
                Contact
              </Link>
            </li>
            <li className="pt-1">
              <a
                href={isHomeRoute ? "#signup" : "/?s=signup"}
                onClick={goTo("signup")}
                className="block rounded-lg bg-white px-4 py-2 text-center text-sm font-semibold text-green-900 shadow hover:shadow-md"
              >
                Join the Launch List
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}


function useScrollToQuery() {
  const { search } = useLocation();
  useEffect(() => {
    const s = new URLSearchParams(search).get("s");
    if (!s) return;
    // Wait for the DOM to paint (especially after route change)
    requestAnimationFrame(() => {
      const el = document.getElementById(s);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [search]);
}


function Section({ id, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      {children}
    </section>
  );
}

function Hero() {
  const scrollToSignup = (e) => {
    e.preventDefault();
    const el = document.getElementById("signup");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Section id="home">
      <div
        className="relative isolate overflow-hidden pt-28 md:pt-32"
        style={{ backgroundColor: BRAND.green }}
      >
        {/* 🌟 Early Adopter Banner (clickable) */}
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <button
            onClick={scrollToSignup}
            className="mx-auto mb-6 block w-full max-w-2xl rounded-xl bg-yellow-300/90 p-3 text-center text-sm font-semibold text-green-900 shadow-md transition hover:bg-yellow-200 hover:shadow-lg"
          >
            <span className="font-bold">
              Early Adopter rollout begins early 2026.
            </span>{" "}
            Limited spaces available - click to join the waitlist!
          </button>
        </div>

        {/* Hero content */}
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 pb-14 md:grid-cols-2 md:px-6 lg:gap-14">
          <div className="text-white">
            <h1 className="text-3xl font-extrabold leading-tight md:text-5xl">
              Protect your flock with{" "}
              <span style={{ color: BRAND.yellow }}>instant alerts</span>
            </h1>
            <p className="mt-4 max-w-xl text-base text-white/85 md:text-lg">
              Our rugged GPS/GSM collar uses on-device AI to detect chase
              events and notify you immediately by call and app notification.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/?s=signup"
                className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-green-900 shadow-lg hover:shadow-xl"
              >
                Join the Waitlist
              </Link>
              <Link
                to="/?s=how"
                className="rounded-xl border border-white/30 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
              >
                See how it works
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4 text-xs text-white/80 md:text-sm">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="font-semibold">AI-powered detection</div>
                <div>Fewer false alarms. Alerts that matter.</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="font-semibold">One collar / up to 200 sheep</div>
                <div>Geofencing & group behaviour insights.</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <CardFrame>
              <img
                src={IMG.hero}
                alt="Sheep Guardian signage: Smart Collar Alerts"
                className="h-full w-full rounded-2xl object-cover"
                loading="eager"
                width="1600"
                height="900"
              />
            </CardFrame>
          </div>
        </div>
      </div>
    </Section>
  );
}


function Problem() {
  return (
    <Section id="problem">
      <div className="bg-white py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 md:grid-cols-2 md:px-6">
          <div className="order-2 md:order-1">
            <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Dog attacks devastate flocks</h2>
            <p className="mt-3 text-gray-700">
              Stress, injuries, missing sheep, and losses add up quickly. Sheep Guardian is designed by farmers to reduce risk
              and respond fast, giving you peace of mind day and night.
            </p>
            <ul className="mt-6 grid gap-3 text-sm text-gray-900/90">
              <li className="flex items-start gap-3"><Check /> Instant phone alerts the moment a chase is detected.</li>
              <li className="flex items-start gap-3"><Check /> GPS breadcrumb trail and live location updates.</li>
              <li className="flex items-start gap-3"><Check /> Works across hills, fields, and remote ground.</li>
            </ul>
          </div>
          <div className="order-1 md:order-2">
            <CardFrame>
              <img
                src={IMG.warning}
                alt="Warning poster showing a dog chasing sheep"
                className="h-full w-full rounded-2xl object-cover"
                loading="lazy"
              />
            </CardFrame>
          </div>
        </div>
      </div>
    </Section>
  );
}

function HowItWorks() {
  return (
    <Section id="how">
      <div className="bg-gray-50 py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 md:grid-cols-[1.05fr_1fr] md:px-6">
          <div>
            <CardFrame>
              <img
                src={IMG.features}
                alt="Poster: GPS Tracking, Geofencing, AI Smart Detection, Instant Notifications"
                className="h-full w-full rounded-2xl object-cover"
                loading="lazy"
              />
            </CardFrame>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">How it works</h2>
            <ol className="mt-5 space-y-4 text-gray-700">
              <li className="flex gap-3"><Bullet>1</Bullet><span><b>On-collar sensors</b> track motion patterns and speed.</span></li>
              <li className="flex gap-3"><Bullet>2</Bullet><span><b>AI on the edge</b> spots chase behaviour and triggers an alert.</span></li>
              <li className="flex gap-3"><Bullet>3</Bullet><span><b>Instant notifications</b> reach your phone by call and push.</span></li>
              <li className="flex gap-3"><Bullet>4</Bullet><span><b>Live GPS</b> guides you straight to the flock.</span></li>
            </ol>
            <div className="mt-6 rounded-2xl border border-emerald-900/10 bg-white p-4">
              <p className="text-sm text-gray-800">
                Designed for scale: one collar can protect a flock by monitoring group movement and geofencing boundary events.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function WhyDifferent() {
  return (
    <Section id="why-different">
      <div className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl grid items-center gap-8 px-4 md:grid-cols-2 md:px-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Not a fleet tracker in disguise</h2>
            <p className="mt-3 text-gray-700">
              Many GPS devices on the market are built for vehicles and re-labelled for livestock. Sheep Guardian isn’t.
              It was engineered from the ground up for sheep, using motion patterns captured across thousands of animals.
            </p>
            <ul className="mt-6 grid gap-3 text-sm text-gray-900/90">
              <li className="flex items-start gap-3"><Check /> <b>Behaviour-AI:</b> self-learning recognises a chase and gets faster as it learns your flock.</li>
              <li className="flex items-start gap-3"><Check /> <b>Real-time alarms:</b> calls and app alerts sent straight to your phone as events happen.</li>
              <li className="flex items-start gap-3"><Check /> <b>Built for fields & hills:</b> rugged housing, long battery, and instant notifications.</li>
              <li className="flex items-start gap-3"><Check /> <b>Global use:</b> works anywhere with mobile coverage.</li>
            </ul>
          </div>
          <CardFrame>
            <img src={IMG.farmer} alt="Sheep Guardian collar close-up, rugged housing" className="h-full w-full rounded-2xl object-contain" />
          </CardFrame>
        </div>
      </div>
    </Section>
  );
}

function ProductShowcase() {
  return (
    <Section id="product">
      <div className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Built for the field</h2>
            <p className="mt-3 text-gray-700">Water-resistant. Rugged. Easy to fit. Long battery life.</p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            <ProductCard src={IMG.render1} title="Rugged housing" desc="Impact-resistant shell with sealed seams" />
            <ProductCard src={IMG.render2} title="Comfortable fit" desc="Smooth strap routing and balanced weight" />
            <ProductCard src={IMG.render3} title="Clear branding" desc="High-visibility mark for theft deterrence" />
          </div>

          <ul className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-3 text-sm text-gray-900/90 md:grid-cols-2">
            <li className="flex items-start gap-3"><Check /> IP-rated enclosure and farm-tough materials.</li>
            <li className="flex items-start gap-3"><Check /> Magnetic charging and secure buckles.</li>
            <li className="flex items-start gap-3"><Check /> Works with Android & iOS Sheep Guardian app.</li>
            <li className="flex items-start gap-3"><Check /> Works globally wherever there’s mobile coverage.</li>
          </ul>
        </div>
      </div>
    </Section>
  );
}

function Story() {
  return (
    <Section id="story">
      <div className="bg-gray-50 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">
            Built on real fields, not in a boardroom
          </h2>

          {/* Stats strip (optional) */}
          <div className="mx-auto mt-6 grid max-w-3xl grid-cols-2 gap-3 text-left md:grid-cols-4 md:text-center">
            <div className="rounded-xl border border-emerald-900/10 bg-white p-4">
              <div className="text-xl font-extrabold text-gray-900">10+ yrs</div>
              <div className="text-xs text-gray-600">of sheep farming</div>
            </div>
            <div className="rounded-xl border border-emerald-900/10 bg-white p-4">
              <div className="text-xl font-extrabold text-gray-900">16</div>
              <div className="text-xs text-gray-600">dog attacks endured</div>
            </div>
            <div className="rounded-xl border border-emerald-900/10 bg-white p-4">
              <div className="text-xl font-extrabold text-gray-900">36</div>
              <div className="text-xs text-gray-600">sheep lost (min.)</div>
            </div>
            <div className="rounded-xl border border-emerald-900/10 bg-white p-4">
              <div className="text-xl font-extrabold text-gray-900">1,200+</div>
              <div className="text-xs text-gray-600">sheep analysed</div>
            </div>
          </div>

          <div className="mx-auto mt-6 max-w-3xl space-y-4 text-left text-gray-700 md:text-center">
            <p>
              Sheep Guardian is built by <b>BT-BroTech</b>. David runs the <b>Sheep School</b> YouTube channel
              and manages <b>1,000+ sheep</b>; Robert leads the technology. Together we turn real farm problems
              into simple, reliable tools.
            </p>

            <p>
              Over the past ten years, David has endured <b>16 dog attacks</b> and <b>36 sheep killed</b>,not
              counting the injured. After trying several market solutions that promised to detect attacks, and
              didn’t, he and Robert decided, over dinner, to build what farmers actually need.
            </p>

            {/* NEW paragraph about other solutions */}
            <p>
              Most livestock trackers we tried are <b>great at GPS location and geofencing</b>, you get a dot on a map and a boundary ping.
              But they <b>don’t recognise a dog chase</b>. Speed spikes, generic motion alerts, or delayed updates miss the pattern of panic.
              By the time you spot it yourself, the damage is done.
            </p>

            <p>
              The plan was simple and hard: combine David’s stock sense with Robert’s engineering. We analysed
              movement from <b>1,200+ sheep</b> across varied terrain, mapping calm grazing, normal flights, and the
              distinct <b>“chase signature.”</b> That field data feeds our <b>behaviour-AI</b> algorithm so
              <b> alerts reach your phone instantly</b>, wherever you are, letting you act in the moments that matter.
            </p>

            <p>
              Responsible dog ownership matters, but reality is different: gates get left open, tourists wander,
              and pets slip out. <b>Sheep Guardian</b> closes the gap between “shouldn’t happen” and “I’m on my way.”
              Built by farmers, for farmers, so you can farm without the constant worry of an undetected dog attack.
            </p>
          </div>

          <div className="mt-8 w-full max-w-4xl mx-auto">
            <div className="relative aspect-video overflow-hidden rounded-2xl shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/qfqKPTxj6uA?si=HbNK_CMHonJwqh4M"
                title="David’s Dog Attack Experience"
                className="absolute inset-0 h-full w-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>



          <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-emerald-900/10 bg-white p-6 text-left shadow-sm">
            <blockquote className="text-gray-900">
              “I know the damage a dog can do in a few minutes. Getting a call the second a chase starts is a game-changer.”
            </blockquote>
            <div className="mt-3 text-sm text-gray-600">— David, sheep farmer & creator of Sheep School</div>
          </div>
        </div>
      </div>
    </Section>
  );
}


/* ---------- SIGNUP (with field-level validation notes) ---------- */

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^(\+?[0-9]{8,15})$/;

function Signup() {
  const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'success' | 'error'
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    phone: false,
    flockSize: false,
    consent: false,
  });
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    flockSize: "",
    message: "",
    consentContact: false,
    consentMarketing: false,
    hp: "",
    startedAt: Date.now(),
  });

  const errors = {
    name: form.name.trim() ? "" : "Please enter your name.",
    email: form.email
      ? emailRe.test(form.email)
        ? ""
        : "Please enter a valid email address."
      : "Email is required.",
    phone: form.phone
      ? phoneRe.test(form.phone)
        ? ""
        : "Enter a valid mobile (0861234567 or +353861234567)."
      : "Mobile number is required.",
    flockSize: form.flockSize ? "" : "Please choose an option.",
    consentContact: form.consentContact
      ? ""
      : "You must agree so we can contact you.",
  };

  const showError = (field) =>
    !!errors[field] && (touched[field] || status === "error");

  async function onSubmit(e) {
    e.preventDefault();

    // Honeypot & basic throttle
    if (form.hp && form.hp.trim().length > 0) {
      setStatus("success");
      return;
    }
    const elapsed = Date.now() - form.startedAt;
    if (elapsed < 1500) {
      setStatus("error");
      return;
    }

    // Mark all as touched so notes show
    setTouched({
      name: true,
      email: true,
      phone: true,
      flockSize: true,
      consent: true,
    });

    // Validate
    const invalid =
      errors.name ||
      errors.email ||
      errors.phone ||
      errors.flockSize ||
      errors.consentContact;
    if (invalid) {
      setStatus("error");
      return;
    }

    setStatus("loading");
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        flockSize: form.flockSize,
        message: form.message || "",
        consentContact: !!form.consentContact,
        consentMarketing: !!form.consentMarketing,
        source: "landing",
        leadStatus: "new",
        leadType: "waitlist",
        createdAt: serverTimestamp(),
        ...buildAttributionFields(),
      };
      const docRef = await addDoc(collection(db, "emailSignups"), payload);
      console.log("Saved lead id:", docRef.id);
      trackWaitlistSubmit(form);
      setStatus("success");
      setTimeout(() => {
        navigate("/thank-you");
      }, 200);
    } catch (err) {
      console.error("Firestore write failed:", err?.code, err?.message);
      setStatus("error");
    }
  }

  return (
    <Section id="signup">
      <div
        className="relative py-16 sm:py-20"
        style={{
          background: `linear-gradient(180deg, ${BRAND.green} 0%, #1a352b 100%)`,
        }}
      >
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          {/* Highlight Banner */}
          <div className="mx-auto mb-6 max-w-2xl rounded-xl bg-yellow-300/90 p-3 text-center text-sm font-semibold text-green-900 shadow-md">
             <span className="font-bold">Early Adopter rollout begins early 2026.</span>{" "}
            Limited spaces available - join today to secure your spot!
          </div>

          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-white">
              Join the Waitlist for Early Adopter Access
            </h2>
            <p className="mt-3 text-white/85 text-base md:text-lg">
              Be among the first farmers to experience{" "}
              <b>Sheep Guardian</b> - our{" "}
              <b>Early Adopter Program</b> begins in{" "}
              <b>early 2026</b>. By joining the waitlist today, you’ll
              receive <b>priority access</b>, exclusive updates, and
              special <b>early-farmer pricing</b> when we launch.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="mx-auto mt-8 grid max-w-3xl gap-4 rounded-2xl bg-white/95 p-6 shadow-lg backdrop-blur"
            noValidate
          >
            <div className="grid gap-4 md:grid-cols-2">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  id="name"
                  required
                  type="text"
                  className={`h-11 w-full rounded-xl border px-3 outline-none ring-emerald-600/10 focus:ring ${
                    showError("name")
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  aria-invalid={!!showError("name")}
                  aria-describedby="name-error"
                  value={form.name}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                />
                {showError("name") && (
                  <p id="name-error" className="mt-1 text-xs text-red-600">
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <div className="relative">
                  <input
                    id="email"
                    required
                    type="email"
                    className={`h-11 w-full rounded-xl border px-3 outline-none ring-emerald-600/10 focus:ring ${
                      showError("email")
                        ? "border-red-500"
                        : form.email
                        ? "border-green-500"
                        : "border-gray-300"
                    }`}
                    aria-invalid={!!showError("email")}
                    aria-describedby="email-error"
                    value={form.email}
                    onBlur={() =>
                      setTouched((t) => ({ ...t, email: true }))
                    }
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                  />
                  {emailRe.test(form.email) && (
                    <span className="absolute inset-y-0 right-3 flex items-center text-green-600">
                      ✔
                    </span>
                  )}
                </div>
                {showError("email") && (
                  <p id="email-error" className="mt-1 text-xs text-red-600">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phone"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Mobile number
                </label>
                <div className="relative">
                  <input
                    id="phone"
                    required
                    type="tel"
                    placeholder="0861234567 or +353861234567"
                    className={`h-11 w-full rounded-xl border px-3 outline-none ring-emerald-600/10 focus:ring ${
                      showError("phone")
                        ? "border-red-500"
                        : form.phone
                        ? "border-green-500"
                        : "border-gray-300"
                    }`}
                    aria-invalid={!!showError("phone")}
                    aria-describedby="phone-error"
                    value={form.phone}
                    onBlur={() =>
                      setTouched((t) => ({ ...t, phone: true }))
                    }
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                  />
                  {phoneRe.test(form.phone) && (
                    <span className="absolute inset-y-0 right-3 flex items-center text-green-600">
                      ✔
                    </span>
                  )}
                </div>
                {showError("phone") ? (
                  <p
                    id="phone-error"
                    className="mt-1 text-xs text-red-600"
                  >
                    {errors.phone}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">
                    Enter your mobile (local or international, e.g.
                    0861234567 or +353861234567)
                  </p>
                )}
              </div>

              {/* Flock size */}
              <div>
                <label
                  htmlFor="flock"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Approx. flock size
                </label>
                <div className="relative">
                  <select
                    id="flock"
                    required
                    className={`h-11 w-full appearance-none rounded-xl border px-3 pr-9 text-gray-900 outline-none ring-emerald-600/10 focus:ring ${
                      showError("flockSize")
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    aria-invalid={!!showError("flockSize")}
                    aria-describedby="flock-error"
                    value={form.flockSize}
                    onBlur={() =>
                      setTouched((t) => ({ ...t, flockSize: true }))
                    }
                    onChange={(e) =>
                      setForm({ ...form, flockSize: e.target.value })
                    }
                  >
                    <option value="">Choose…</option>
                    <option>Under 100</option>
                    <option>100–200</option>
                    <option>200–400</option>
                    <option>400–800</option>
                    <option>800+</option>
                    <option>No sheep, interested in product</option>
                  </select>
                  <svg
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z" />
                  </svg>
                </div>
                {showError("flockSize") && (
                  <p
                    id="flock-error"
                    className="mt-1 text-xs text-red-600"
                  >
                    {errors.flockSize}
                  </p>
                )}
              </div>

              {/* Optional message */}
              <div className="md:col-span-2">
                <label
                  htmlFor="message"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Anything we should know?{" "}
                  <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  id="message"
                  type="text"
                  placeholder="Breeds, terrain, issues you face…"
                  className="h-11 w-full rounded-xl border border-gray-300 px-3 outline-none ring-emerald-600/10 focus:ring"
                  value={form.message}
                  onChange={(e) =>
                    setForm({ ...form, message: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Honeypot (hidden) */}
            <div className="sr-only" aria-hidden="true">
              <label>Company</label>
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.hp}
                onChange={(e) =>
                  setForm({ ...form, hp: e.target.value })
                }
              />
            </div>

            {/* Consent + GDPR */}
            <div className="space-y-3 rounded-xl bg-white/70 p-3 text-sm text-gray-700">
              <label className="flex items-start gap-2">
                <input
                  required
                  type="checkbox"
                  className={`mt-1 h-4 w-4 ${
                    showError("consentContact")
                      ? "ring-2 ring-red-400"
                      : ""
                  }`}
                  checked={form.consentContact}
                  onBlur={() =>
                    setTouched((t) => ({ ...t, consent: true }))
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      consentContact: e.target.checked,
                    })
                  }
                />
                <span>
                  I agree to be contacted about becoming an early adopter
                  and for essential updates about Sheep Guardian.
                </span>
              </label>
              {showError("consentContact") && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.consentContact}
                </p>
              )}

              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={form.consentMarketing}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      consentMarketing: e.target.checked,
                    })
                  }
                />
                <span>
                  Also send me occasional news about launch dates and
                  offers (optional).
                </span>
              </label>

              <p className="text-xs text-gray-500">
                We use your information only for Sheep Guardian
                communications. You can unsubscribe anytime.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-4 pt-2">
              <div className="text-xs text-gray-500">
                We’ll only email about Sheep Guardian.
              </div>
              <button
                type="submit"
                disabled={status === "loading" || status === "success"}
                className="rounded-xl bg-green-900 px-5 py-3 text-sm font-semibold text-white shadow hover:shadow-md disabled:opacity-60"
                style={{ backgroundColor: BRAND.green }}
              >
                {status === "loading"
                  ? "Submitting…"
                  : status === "success"
                  ? "You're on the Early Access List!"
                  : "Join the Waitlist"}
              </button>
            </div>

            {status === "error" && (
              <p className="text-sm font-medium text-red-600">
                Please check the highlighted fields and try again.
              </p>
            )}
          </form>
        </div>
      </div>
    </Section>
  );
}


function Footer() {
  return (
    <footer className="bg-white py-8">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 text-center text-sm text-gray-600 md:flex-row md:text-left md:px-6">
        <div>© {new Date().getFullYear()} BT-BroTech. All rights reserved.</div>
        <div className="flex items-center gap-4">
          <Link to="/?s=home" className="hover:text-gray-900">Top</Link>
          <a href="https://www.sheepguardian.com" className="hover:text-gray-900" target="_blank" rel="noreferrer">sheepguardian.com</a>
          <div className="ml-2 flex items-center gap-1">
            <a href={SOCIAL.x} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" title="X (Twitter)" className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100 text-gray-700">
              <IconX />
            </a>
            <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" title="Facebook" className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100 text-gray-700">
              <IconFacebook />
            </a>
            <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="Instagram" className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100 text-gray-700">
              <IconInstagram />
            </a>
            <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn" className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100 text-gray-700">
              <IconLinkedIn />
            </a>
          </div>
        </div>
      </div>
    </footer>

  );
}

// --- small UI helpers ---
function CardFrame({ children }) {
  return (
    <div className="relative rounded-3xl border border-emerald-900/10 bg-white p-2 shadow-sm">
      <div className="overflow-hidden rounded-2xl">{children}</div>
    </div>
  );
}

function ProductCard({ src, title, desc }) {
  return (
    <div className="rounded-2xl border border-emerald-900/10 bg-white p-3 shadow-sm">
      <div className="aspect-[16/7] w-full overflow-hidden rounded-xl bg-gray-50">
        <img src={src} alt={title} className="h-full w-full object-contain" loading="lazy" />
      </div>
      <div className="p-3">
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-600">{desc}</div>
      </div>
    </div>
  );
}

function Check() {
  return (
    <svg
      className="mt-1 h-5 w-5 flex-none text-green-700"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function Bullet({ children }) {
  return (
    <div
      className="flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold text-green-900"
      style={{ backgroundColor: BRAND.yellow }}
    >
      {children}
    </div>
  );
}

/* ---------- Pages ---------- */

export default function SheepGuardianLanding() {
  useScrollToQuery();

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar />
      <main>
        <Hero />
        <Problem />
        <HowItWorks />
        <WhyDifferent />
        <ProductShowcase />
        <Story />
        <Signup />
      </main>
      <Footer />
    </div>
  );
}

export function ThankYou() {
  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${BRAND.green} 0%, #1a352b 100%)` }}>
      <Navbar rootLinks />
      <main className="mx-auto max-w-3xl px-6 py-16 text-center text-white">
        <h1 className="text-4xl font-extrabold">You're on the list ✅</h1>
        <p className="mt-4 text-white/90">
          Thanks for joining the launch list. We’ll be in touch about early-farmer access and next steps.
        </p>
        <div className="mx-auto mt-8 max-w-xl rounded-2xl bg-white/10 p-6">
          <p className="text-white/90">
            While you wait, watch David’s story of a real dog attack and how fast response matters.
          </p>
          <div className="mt-8 w-full max-w-4xl mx-auto">
            <div className="relative aspect-video overflow-hidden rounded-2xl shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/qfqKPTxj6uA?si=HbNK_CMHonJwqh4M"
                title="David’s Dog Attack Experience"
                className="absolute inset-0 h-full w-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </div>
        <div className="mt-10">
          <Link to="/?s=home" className="rounded-xl border border-white/40 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10">
            Back to homepage
          </Link>
        </div>
      </main>
      <footer className="pb-10 text-center text-sm text-white/70">© {new Date().getFullYear()} BT-BroTech</footer>
    </div>
  );
}

export function Contact() {
  const [status, setStatus] = useState("idle");
  const [touched, setTouched] = useState({ name: false, email: false, message: false });
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });

  // make sure emailRe is defined in this file (or import it)
  // const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const errs = {
    name: f.name.trim() ? "" : "Please enter your name.",
    email: f.email ? (emailRe.test(f.email) ? "" : "Please enter a valid email address.") : "Email is required.",
    message: f.message.trim() ? "" : "Let us know how we can help.",
  };
  const show = (k) => !!errs[k] && (touched[k] || status === "error");

  async function submit(e) {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (errs.name || errs.email || errs.message) { setStatus("error"); return; }

    setStatus("loading");
    try {
      const payload = {
        name: f.name.trim(),
        email: f.email.trim(),
        phone: f.phone?.trim() || null,
        message: f.message.trim(),
        leadStatus: "new",
        leadType: "contact",
        createdAt: serverTimestamp(),
        ...buildAttributionFields(),
      };
      const ref = await addDoc(collection(db, "contactMessages"), payload);
      console.log("contact saved:", ref.id);
      trackContactSubmit(f);
      setStatus("success");
      // optional: reset the form
      // setF({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error("Firestore write failed:", err);
      setStatus("error");
    }
  }

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${BRAND.green} 0%, #1a352b 100%)` }}>
      <Navbar rootLinks />
      <main className="mx-auto max-w-3xl px-6 pt-28 md:pt-32 pb-12 text-white">
        <h1 className="text-3xl font-extrabold">Contact</h1>
        <p className="mt-2 text-white/90">
          For enquiries or more info, email <a className="underline" href="mailto:info@sheepguardian.com">info@sheepguardian.com</a> or use the form below.
        </p>

        <form onSubmit={submit} className="mt-6 grid gap-4 rounded-2xl bg-white/95 p-6 text-gray-900 shadow-lg backdrop-blur" noValidate>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Name</label>
              <input
                required
                autoComplete="name"
                className={`h-11 w-full rounded-xl border px-3 outline-none ring-emerald-600/10 focus:ring ${show("name") ? "border-red-500" : "border-gray-300"}`}
                value={f.name}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                onChange={(e) => setF({ ...f, name: e.target.value })}
              />
              {show("name") && <p className="mt-1 text-xs text-red-600">{errs.name}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Email</label>
              <input
                required
                type="email"
                autoComplete="email"
                className={`h-11 w-full rounded-xl border px-3 outline-none ring-emerald-600/10 focus:ring ${show("email") ? "border-red-500" : f.email ? "border-green-500" : "border-gray-300"}`}
                value={f.email}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                onChange={(e) => setF({ ...f, email: e.target.value })}
              />
              {show("email") && <p className="mt-1 text-xs text-red-600">{errs.email}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium">Phone (optional)</label>
              <input
                type="tel"
                autoComplete="tel"
                className="h-11 w-full rounded-xl border border-gray-300 px-3 outline-none ring-emerald-600/10 focus:ring"
                value={f.phone}
                onChange={(e) => setF({ ...f, phone: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium">How can we help?</label>
              <textarea
                required
                rows={5}
                className={`w-full rounded-xl border px-3 py-2 outline-none ring-emerald-600/10 focus:ring ${show("message") ? "border-red-500" : "border-gray-300"}`}
                value={f.message}
                onBlur={() => setTouched((t) => ({ ...t, message: true }))}
                onChange={(e) => setF({ ...f, message: e.target.value })}
              />
              {show("message") && <p className="mt-1 text-xs text-red-600">{errs.message}</p>}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <a className="text-sm text-emerald-50 underline" href="mailto:info@sheepguardian.com">Prefer email? info@sheepguardian.com</a>
            <button
              type="submit"
              disabled={status === "loading" || status === "success"}
              className="rounded-xl bg-green-900 px-5 py-3 text-sm font-semibold text-white shadow disabled:opacity-60"
              style={{ backgroundColor: BRAND.green }}
            >
              {status === "loading" ? "Sending…" : status === "success" ? "Sent – thanks!" : "Send message"}
            </button>
          </div>

          {status === "error" && <p className="text-sm font-medium text-red-600">Please fill the required fields and try again.</p>}
          {status === "success" && <p className="text-sm font-medium text-green-700">We’ve received your message.</p>}
        </form>
      </main>
      <footer className="pb-10 text-center text-sm text-white/70">© {new Date().getFullYear()} BT-BroTech</footer>
    </div>
  );
}

